import React from "react"

// 类型定义
export interface DialogCustomOptions {
    component: React.ComponentType<any>
    width?: number | string
    maskClosable?: boolean
    title?: string
    showIcon?: boolean
    showClose?: boolean
    icon?: any
    maskBg?: string
    props?: Record<string, any>
    onNext?: (value?: any) => void
    familyName?: string // 支持多弹窗的命名空间
    style?: React.CSSProperties
}

// 弹窗状态类型
type DialogState = {
    visible: boolean // 是否显示
    options: DialogCustomOptions | null // 弹窗配置
    resolve?: (value: any) => void // 成功回调
    reject?: (reason?: any) => void // 失败回调
}

// 多弹窗状态管理
type DialogStateMap = Map<string, DialogState>

// 模块级全局状态与订阅（替代 @preact/signals）
let dialogStates: DialogStateMap = new Map()
const listeners = new Set<() => void>()

function getSnapshot(): DialogStateMap {
    return dialogStates
}

function subscribe(listener: () => void): () => void {
    listeners.add(listener)
    return () => listeners.delete(listener)
}

function emit() {
    listeners.forEach((listener) => listener())
}

// 默认family名称
const DEFAULT_FAMILY = "default"

// 获取滚动条宽度
function getScrollbarWidth(): number {
    const outer = document.createElement("div")
    outer.style.visibility = "hidden"
    outer.style.overflow = "scroll"
    // @ts-ignore - msOverflowStyle is for IE compatibility
    outer.style.msOverflowStyle = "scrollbar"
    document.body.appendChild(outer)

    const inner = document.createElement("div")
    outer.appendChild(inner)

    const scrollbarWidth = outer.offsetWidth - inner.offsetWidth
    document.body.removeChild(outer)

    return scrollbarWidth
}

// 管理body滚动条的工具类
class ScrollbarManager {
    private static originalStyles: {
        overflow: string
        paddingRight: string
    } | null = null

    private static openCount = 0

    static lockScroll() {
        this.openCount++

        if (this.openCount === 1) {
            // 第一个弹窗打开时记录原始样式
            this.originalStyles = {
                overflow: document.body.style.overflow,
                paddingRight: document.body.style.paddingRight,
            }

            // 隐藏滚动条并添加padding补偿
            const scrollbarWidth = getScrollbarWidth()
            document.body.style.overflow = "hidden"
            document.body.style.paddingRight = `${scrollbarWidth}px`
        }
    }

    static unlockScroll() {
        this.openCount = Math.max(0, this.openCount - 1)

        if (this.openCount === 0 && this.originalStyles) {
            // 所有弹窗都关闭时恢复原始样式
            document.body.style.overflow = this.originalStyles.overflow
            document.body.style.paddingRight = this.originalStyles.paddingRight
            this.originalStyles = null
        }
    }
}

// 获取指定family的弹窗状态
function getDialogState(familyName: string): DialogState {
    const states = dialogStates
    const existing = states.get(familyName)

    if (existing) {
        return existing
    }

    // 创建新的状态
    const newState: DialogState = {
        visible: false,
        options: null,
        resolve: undefined,
        reject: undefined,
    }

    const newStates = new Map(states)
    newStates.set(familyName, newState)
    dialogStates = newStates
    emit()

    return newState
}

// 更新弹窗状态
function updateDialogState(familyName: string, updates: Partial<DialogState>) {
    const states = dialogStates
    const currentState = states.get(familyName)

    if (currentState) {
        const newStates = new Map(states)
        newStates.set(familyName, { ...currentState, ...updates })
        dialogStates = newStates
        emit()
    }
}

// 主弹窗函数
export function openDialogCustom(options: DialogCustomOptions): Promise<any> {
    const familyName = options.familyName || DEFAULT_FAMILY

    return new Promise<any>((resolve, reject) => {
        // 确保状态存在
        getDialogState(familyName)

        // 锁定滚动条
        ScrollbarManager.lockScroll()

        // 关闭函数
        const closeDialog = (value?: any, isCancel = false) => {
            updateDialogState(familyName, { visible: false })

            setTimeout(() => {
                // 解锁滚动条
                ScrollbarManager.unlockScroll()

                // 清理状态
                updateDialogState(familyName, {
                    options: null,
                    resolve: undefined,
                    reject: undefined,
                })

                // 返回结果
                if (isCancel) {
                    reject(new Error("弹窗被取消"))
                } else {
                    resolve(value)
                }
            }, 300) // 等待动画完成
        }

        // 更新状态
        updateDialogState(familyName, {
            visible: true,
            options: {
                ...options,
                familyName,
            },
            resolve: (value: any) => closeDialog(value, false),
            reject: () => closeDialog(null, true),
        })
    })
}

// 静态方法集合
export const DialogCustomStaticMethods = {
    /**
     * 打开弹窗
     */
    open: (
        component: React.ComponentType<any>,
        options?: Omit<DialogCustomOptions, "component">,
    ): Promise<any> => {
        return openDialogCustom({
            component,
            ...options,
        })
    },

    /**
     * 打开指定family的弹窗
     */
    openWithFamily: (
        familyName: string,
        component: React.ComponentType<any>,
        options?: Omit<DialogCustomOptions, "component" | "familyName">,
    ): Promise<any> => {
        return openDialogCustom({
            component,
            familyName,
            ...options,
        })
    },

    /**
     * 关闭指定family的弹窗
     */
    close: (familyName: string = DEFAULT_FAMILY): void => {
        const state = getDialogState(familyName)
        if (state.visible) {
            state.reject?.()
        }
    },

    /**
     * 关闭所有弹窗
     */
    closeAll: (): void => {
        const states = dialogStates
        states.forEach((state) => {
            if (state.visible) {
                state.reject?.()
            }
        })
    },

    /**
     * 获取所有弹窗状态
     */
    getStates: (): DialogStateMap => {
        return dialogStates
    },

    /**
     * 检查是否有弹窗打开
     */
    hasOpenDialog: (familyName?: string): boolean => {
        const states = dialogStates

        if (familyName) {
            const state = states.get(familyName)
            return state?.visible ?? false
        }

        // 检查是否有任何弹窗打开
        return Array.from(states.values()).some((state) => state.visible)
    },
}

// 底层订阅
export function useDialogStates(): DialogStateMap {
    return React.useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}

// 选择器 Hook：对外仅暴露派生结果，避免暴露底层可变 Map
export function useDialogSelector<T>(selector: (states: DialogStateMap) => T): T {
    return React.useSyncExternalStore(
        subscribe,
        () => selector(getSnapshot()),
        () => selector(getSnapshot()),
    )
}

// 是否存在任意弹窗打开
export function useHasOpenDialog(): boolean {
    return useDialogSelector((states) => Array.from(states.values()).some((s) => s.visible))
}

// 指定 family 是否打开
export function useFamilyVisible(familyName: string): boolean {
    return useDialogSelector((states) => states.get(familyName)?.visible === true)
}

// 一次性返回只读快照（类型只读；不做运行时冻结，避免开销）
export function getDialogFrozenSnapshot(): ReadonlyMap<
    string,
    Readonly<{ visible: boolean; options: DialogCustomOptions | null }>
> {
    return dialogStates as unknown as ReadonlyMap<
        string,
        Readonly<{ visible: boolean; options: DialogCustomOptions | null }>
    >
}

// 非 React 环境订阅：监听 hasOpen 的变化
export function subscribeHasOpen(listener: (hasOpen: boolean) => void): () => void {
    const compute = () => Array.from(dialogStates.values()).some((s) => s.visible)
    let prev = compute()
    // 立刻回调一次，告知当前值
    listener(prev)
    const wrapped = () => {
        const next = compute()
        if (next !== prev) {
            prev = next
            listener(next)
        }
    }
    const unsub = subscribe(wrapped)
    return () => unsub()
}

// 保持原来的函数名
export const useDialogCustom = openDialogCustom

// 导出默认函数
export default openDialogCustom
